import PropTypes from "prop-types"
import { get, map } from "lodash"
import OPTooltip from "components/material/tooltip"
import { getDriftType, findTradeStatus, getDriftIdByTradeStatus, computeDrift, computeDriftValues } from "helpers/drift"
import DriftIcon from "./driftIcon"
import { ITEM_SHAPE, VALUE_SHAPE } from "./prop-types"

function normalizeRiskValue(risk, dataPath) {
  // We multiply predicted risk for display purposes so we need to divide it back to get the actual value
  if (dataPath.includes("predictedRisk") && risk) {
    return risk / 100
  }
  return risk
}

function computeDriftData(contribution, metricValue, item, metricLabel) {
  if (!contribution || !metricValue) {
    return { driftType: null, title: null }
  }

  const contributionValues = map(contribution, (value) => value?.value ? parseFloat(value.value) : parseFloat(value))
  if (contributionValues.length === 0) {
    return { driftType: null, title: null }
  }
  const { mean, stdDev } = computeDriftValues(contributionValues)
  const drift = computeDrift(mean, stdDev, parseFloat(metricValue))
  const driftType = getDriftType(drift)
  const itemLabel = item.category || item.label
  const title = itemLabel && `${metricLabel} drift for ${itemLabel} is ${driftType?.label}`

  return { driftType, title }
}

export function FactorPerformanceDriftWithTooltip({ item, value, dataPath }) {
  const drift = get(item.values, dataPath, 0)
  const driftType = findTradeStatus(drift)
  const driftId = getDriftIdByTradeStatus(driftType)

  const itemLabel = item.category || item.label
  const title = itemLabel && `${itemLabel} is ${driftType}`

  return <DriftWithTooltip title={title} driftId={driftId}>
    {value}
  </DriftWithTooltip>
}

export function FactorRiskDriftWithTooltip({ item, value, label: metricLabel, metric }) {
  const dataPath = metric.dataPath
  const contribution = get(item.values, `${dataPath}Contribution`)
  let risk = get(item.values, dataPath)
  risk = normalizeRiskValue(risk, dataPath)

  const { driftType, title } = computeDriftData(contribution, risk, item, metricLabel)

  return <DriftWithTooltip title={title} driftId={driftType?.id}>
    {value}
  </DriftWithTooltip>
}

export function FactorBetaDriftWithTooltip({ item, value, metric }) {
  const dataPath = metric.dataPath
  const beta = get(item.values, `${dataPath}.value`, 0)
  const contribution = get(item.values, `${dataPath}.contribution`, [])

  const { driftType, title } = computeDriftData(contribution, beta, item, "Beta")

  return <DriftWithTooltip title={title} driftId={driftType?.id}>
    {value}
  </DriftWithTooltip>
}

function DriftWithTooltip({ title, children, driftId }) {
  return <OPTooltip title={title} className="drift-icon-tooltip">
    {children || "-"}
    <DriftIcon driftId={driftId} />
  </OPTooltip>
}

FactorPerformanceDriftWithTooltip.propTypes = {
  item: ITEM_SHAPE.isRequired,
  value: VALUE_SHAPE.isRequired,
  dataPath: PropTypes.string.isRequired,
}

FactorRiskDriftWithTooltip.propTypes = {
  item: ITEM_SHAPE.isRequired,
  value: VALUE_SHAPE.isRequired,
  dataPath: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  metric: PropTypes.shape({
    dataPath: PropTypes.string.isRequired,
  }).isRequired,
}

FactorBetaDriftWithTooltip.propTypes = {
  item: ITEM_SHAPE.isRequired,
  value: VALUE_SHAPE.isRequired,
  dataPath: PropTypes.string.isRequired,
  metric: PropTypes.shape({
    dataPath: PropTypes.string.isRequired,
  }).isRequired,
}

DriftWithTooltip.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  children: PropTypes.node,
  driftId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
}
